import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  OBLIGATION_FILTER_OPTIONS,
  PERIOD_MODE_OPTIONS,
  SCOPE_OPTIONS,
} from "@/modules/despacho/complianceInstanceHelpers";
import ComplianceInstanceDetailDialog from "@/pages/despacho/ComplianceInstanceDetailDialog";

function flattenBoardClients(groups) {
  const out = [];
  for (const g of groups || []) {
    for (const row of g.items || []) {
      if (row?.fiscal_client_id) {
        out.push({
          id: row.fiscal_client_id,
          name: row.razon_social || row.nombre || row.client_name || row.fiscal_client_id,
        });
      }
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export function CalendarioProximosPanel({ readOnly = false, scope, dateFrom = "", dateTo = "" }) {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadItems = useCallback(async () => {
    setLoadState("loading");
    setLoadMessage("");
    try {
      const rows = await despachoFiscalInfoApi.listComplianceInstances({ upcoming: true });
      setItems(rows);
      setLoadState("ready");
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      setItems([]);
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parsed.message);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openDetail = (row) => {
    setDetailId(row.id);
    setDetailOpen(true);
  };

  const quickStatus = async (row, status) => {
    if (readOnly) return;
    try {
      await despachoFiscalInfoApi.patchComplianceInstance(row.id, { status });
      toast.success("Estado actualizado.");
      await loadItems();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const filteredItems = useMemo(() => {
    let rows = items;
    if (scope === "fiscal") {
      rows = rows.filter((r) => String(r.authority || "") !== "Interna");
    } else if (scope === "operational") {
      rows = rows.filter((r) => String(r.authority || "") === "Interna");
    }
    if (dateFrom || dateTo) {
      rows = rows.filter((r) => {
        const fd = String(r.fecha_fiscal || r.due_date || "").slice(0, 10);
        if (!fd) return false;
        if (dateFrom && fd < dateFrom) return false;
        if (dateTo && fd > dateTo) return false;
        return true;
      });
    }
    return rows;
  }, [items, scope, dateFrom, dateTo]);

  const columns = useMemo(
    () => [
      { key: "fecha_fiscal", label: "Fecha fiscal", render: (r) => r.fecha_fiscal || r.due_date || "—" },
      { key: "client_name", label: "Cliente", render: (r) => r.client_name || r.client_rfc || "—" },
      { key: "obligation_name", label: "Obligación", render: (r) => r.obligation_name || "—" },
      { key: "period", label: "Periodo", render: (r) => r.period || r.period_key || "—" },
      {
        key: "assigned_auxiliar_name",
        label: "Auxiliar",
        render: (r) => r.assigned_auxiliar_name || "—",
      },
      {
        key: "status",
        label: "Estado",
        render: (r) => (
          <DespachoStatusBadge variant="secondary">{r.status_label || r.status || "—"}</DespachoStatusBadge>
        ),
      },
      {
        key: "actions",
        label: "Acción",
        render: (r) => (
          <div className="flex flex-wrap gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => openDetail(r)}>
              Ver
            </Button>
            {!readOnly ? (
              <>
                <Button type="button" size="sm" variant="ghost" onClick={() => quickStatus(r, "requested")}>
                  Solicitado
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => quickStatus(r, "in_progress")}>
                  En proceso
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => quickStatus(r, "done")}>
                  Terminado
                </Button>
              </>
            ) : null}
          </div>
        ),
      },
    ],
    [readOnly],
  );

  return (
    <div className="space-y-4">
      <DespachoInfoCallout>
        {scope === "operational"
          ? "Próximos vencimientos operativos internos por empresa. Use las acciones para actualizar el estado."
          : "Próximos vencimientos fiscales generados por empresa y obligación. Use las acciones para actualizar el estado o abrir el detalle."}
      </DespachoInfoCallout>

      {loadState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando vencimientos…
        </div>
      ) : null}

      {loadState === "error" || loadState === "forbidden" ? (
        <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p className="m-0">{loadMessage}</p>
        </div>
      ) : null}

      {loadState === "ready" ? (
        <DespachoSectionCard title="Próximos vencimientos" description={`${filteredItems.length} instancia(s)`}>
          <DespachoSimpleTable columns={columns} rows={filteredItems} emptyLabel="No hay vencimientos pendientes." />
        </DespachoSectionCard>
      ) : null}

      <ComplianceInstanceDetailDialog
        instanceId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={loadItems}
        readOnly={readOnly}
      />
    </div>
  );
}

export function CalendarioGeneratePanel({ readOnly = false, onGenerated }) {
  const [periodMode, setPeriodMode] = useState("current_month");
  const [scope, setScope] = useState("all");
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [includeFallback, setIncludeFallback] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewState, setPreviewState] = useState("idle");
  const [generateBusy, setGenerateBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const board = await despachoFiscalInfoApi.getBoard();
        setClients(flattenBoardClients(board.groups));
      } catch {
        setClients([]);
      }
    })();
  }, []);

  const buildBody = () => ({
    period_mode: periodMode,
    fiscal_client_ids: scope === "one" && clientId ? [clientId] : undefined,
    include_fallback: includeFallback,
    confirm: false,
  });

  const runPreview = async () => {
    if (scope === "one" && !clientId) {
      toast.error("Seleccione una empresa.");
      return;
    }
    setPreviewState("loading");
    setPreview(null);
    try {
      const data = await despachoFiscalInfoApi.previewGenerateCompliance(buildBody());
      setPreview(data);
      setPreviewState("ready");
    } catch (err) {
      setPreviewState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const confirmGenerate = async () => {
    if (readOnly) return;
    setGenerateBusy(true);
    try {
      const result = await despachoFiscalInfoApi.generateCompliance({
        ...buildBody(),
        confirm: true,
      });
      toast.success(`Se crearon ${result.created ?? 0} instancia(s).`);
      setPreview(null);
      setPreviewState("idle");
      onGenerated?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setGenerateBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="calendario-generar-vencimientos">
      <DespachoInfoCallout>
        Genere instancias fiscales por empresa a partir de las reglas del Calendario. Revise la vista previa
        antes de confirmar; no se crean duplicados. Las tareas operativas se generan en el panel inferior.
      </DespachoInfoCallout>

      <DespachoSectionCard title="Opciones de generación">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label>Periodo</Label>
            <Select value={periodMode} onValueChange={setPeriodMode}>
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Alcance</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {scope === "one" ? (
            <div className="space-y-2">
              <Label>Empresa</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Seleccione…" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label>Obligaciones</Label>
            <p className="text-sm text-slate-600 m-0">{OBLIGATION_FILTER_OPTIONS[0].label}</p>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Checkbox
              id="include-fallback"
              checked={includeFallback}
              onCheckedChange={(v) => setIncludeFallback(Boolean(v))}
            />
            <Label htmlFor="include-fallback" className="font-normal cursor-pointer">
              Incluir regla fallback global
            </Label>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button type="button" onClick={runPreview} disabled={previewState === "loading"}>
            {previewState === "loading" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
                Calculando…
              </>
            ) : (
              "Vista previa"
            )}
          </Button>
        </div>
      </DespachoSectionCard>

      {preview ? (
        <DespachoSectionCard
          title="Vista previa"
          description={`${preview.summary?.new_count ?? 0} nuevas · ${preview.summary?.duplicate_count ?? 0} duplicados ignorados`}
        >
          <div className="text-sm space-y-3 mb-4">
            <p className="m-0">
              Periodos: <strong>{(preview.period_keys || []).join(", ") || "—"}</strong>
            </p>
            <p className="m-0">
              Empresas incluidas: <strong>{(preview.clients_included || []).length}</strong>
            </p>
            {(preview.skipped_fallback || []).length ? (
              <p className="m-0 text-amber-800">
                Obligaciones con fallback omitidas: {preview.skipped_fallback.length}
              </p>
            ) : null}
          </div>
          <DespachoSimpleTable
            columns={[
              { key: "client", label: "Cliente", render: (r) => r.fiscal_client_id },
              { key: "obligation_name", label: "Obligación" },
              { key: "period", label: "Periodo" },
              { key: "fecha_fiscal", label: "Fecha fiscal" },
              {
                key: "rule_source",
                label: "Regla",
                render: (r) => (r.rule_source === "fallback_global_rule" ? "Fallback" : "Específica"),
              },
            ]}
            rows={(preview.instances_to_create || []).slice(0, 50).map((r, i) => ({ ...r, id: `${r.fiscal_client_id}-${i}` }))}
            emptyLabel="No hay instancias nuevas para crear."
          />
          {!readOnly ? (
            <div className="mt-4">
              <Button type="button" onClick={confirmGenerate} disabled={generateBusy || !(preview.instances_to_create || []).length}>
                {generateBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
                    Generando…
                  </>
                ) : (
                  "Confirmar generación"
                )}
              </Button>
            </div>
          ) : null}
        </DespachoSectionCard>
      ) : null}
    </div>
  );
}
